import { Service, val, func } from 'beatlejs';
import { signal } from 'beatlejs/decorators/signal';
import { debounce } from 'beatlejs/plugins/debounce';
import api from '@/utils/axios';
import { isSuccessResponse } from '@/utils/utils';

// API Types based on actual DTOs
export interface LoginAdminDto {
  username: string;
  password: string;
}

export interface AdminUser {
  id: string;
  username: string;
  name?: string;
  permissions: string[];
  more: {
    role: string;
  },
  role: string;
}

type LoginForm = {
  username: string;
  password: string;
  showPassword: boolean;
};

type LoginFormField = keyof LoginForm;

export interface AuthService {
  // State
  isLoggedIn: boolean;
  user: AdminUser | null;
  isLoading: boolean;
  error: string | null;

  // Login form
  loginForm: LoginForm;

  // Actions
  setLoginForm: (field: LoginFormField, value: string | boolean) => void;
  login: () => Promise<boolean>;
  logout: () => void;
  checkAuthStatus: () => Promise<boolean>;
  clearError: () => void;
}

const defaultLoginForm: LoginForm = {
  username: '',
  password: '',
  showPassword: false,
};

function normalizeAdminUser(admin: unknown): AdminUser | null {
  if (typeof admin !== 'object' || admin === null) return null;

  const candidate = admin as Record<string, unknown>;
  const more =
    typeof candidate.more === 'object' && candidate.more !== null
      ? (candidate.more as Record<string, unknown>)
      : {};
  const role =
    typeof candidate.role === 'string'
      ? candidate.role
      : typeof more.role === 'string'
        ? more.role
        : '';

  return {
    id: typeof candidate.id === 'string' ? candidate.id : '',
    username:
      typeof candidate.username === 'string' ? candidate.username : '',
    ...(typeof candidate.name === 'string' ? { name: candidate.name } : {}),
    permissions: Array.isArray(candidate.permissions)
      ? candidate.permissions.filter(
          (permission): permission is string => typeof permission === 'string',
        )
      : [],
    more: { role },
    role,
  };
}

const self = Service({ identifier: 'Auth' }, {
  isLoggedIn: signal(val(false)),
  user: signal(val(null as AdminUser | null)),
  isLoading: signal(val(false)),
  error: signal(val(null as string | null)),
  loginForm: signal(val(defaultLoginForm)),

  setLoginForm: func(setLoginForm),
  login: func(login),
  logout: debounce(func(logout)),
  checkAuthStatus: func(checkAuthStatus),
  clearError: func(clearError),
});

function setLoginForm(
  this: typeof self,
  field: LoginFormField,
  value: string | boolean,
) {
  if (field === 'showPassword') {
    this.loginForm = {
      ...this.loginForm,
      showPassword: typeof value === 'boolean' ? value : false,
    };
    return;
  }
  this.loginForm = {
    ...this.loginForm,
    [field]: typeof value === 'string' ? value : '',
  };
}

async function login(this: typeof self): Promise<boolean> {
  this.isLoading = true;
  this.error = null;

  try {
    const loginData: LoginAdminDto = {
      username: this.loginForm.username,
      password: this.loginForm.password,
    };

    const response = await api.post('/admins/login', loginData);
    if (isSuccessResponse(response.data.statusCode)) {
      this.isLoggedIn = true;
      this.loginForm = defaultLoginForm;
      this.user = normalizeAdminUser(response.data.data.admin);
      return true;
    } else {
      this.error = response.data.message || 'ورود ناموفق بود';
      this.isLoggedIn = false;
      this.user = null;
      return false;
    }
  } catch (error) {
    this.error = authErrorMessage(error, 'خطا در ورود به سیستم');
    this.isLoggedIn = false;
    this.user = null;
    return false;
  } finally {
    this.isLoading = false;
  }
}

async function logout(this: typeof self): Promise<void> {
  try {
    // POST /admin/logout → clears cookies server-side
    await api.post('/admins/logout');
  } catch {
    // Local state must still be cleared when the server session has expired.
  }

  // Reset state locally
  this.isLoggedIn = false;
  this.user = null;
  this.loginForm = defaultLoginForm;
  this.error = null;
}

async function checkAuthStatus(this: typeof self): Promise<boolean> {
  // If we already have a user in memory and are logged in, return true
  if (this.user && this.isLoggedIn) {
    return true;
  }

  // GET /admin/auth-check → returns 200 if cookie valid
  try {
    const response = await api.get('/admins/auth-check');

    if (isSuccessResponse(response.status) && response.data?.data) {
      // If the API call succeeds, we're authenticated
      this.isLoggedIn = true;
      this.user = normalizeAdminUser(response.data.data);
      return true;
    }
  } catch (error) {
    // If API call fails (401), the axios interceptor will handle token refresh
    // If refresh fails, user will be redirected to login

    // Don't immediately set logged out - let axios interceptor handle refresh
    if (authErrorStatus(error) !== 401) {
      this.isLoggedIn = false;
      this.user = null;
    }
  }

  return false;
}

function clearError(this: typeof self): void {
  this.error = null;
}

function authErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null || !('status' in response)) {
    return undefined;
  }
  const status = (response as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

function authErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return fallback;
  }
  const data = (response as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return fallback;
  }
  const message = (data as { message?: unknown }).message;
  if (Array.isArray(message)) {
    return message.filter((item): item is string => typeof item === 'string').join('، ') || fallback;
  }
  return typeof message === 'string' && message.trim() ? message : fallback;
}

export const $Auth = self;
